import { TrashIcon } from "@heroicons/react/24/outline";
import type { Control, FieldValues } from "react-hook-form";

import ButtonNewItem from "~/components/buttons/ButtonNewItem/ButtonNewItem.tsx";
import CardValue from "~/components/Card/components/CardValue.tsx";
import CurrencyInput from "~/components/formElements/CurrencyInput.tsx";
import FormInputError from "~/components/formElements/FormInputError.tsx";
import FormSection from "~/components/formElements/FormSection";
import Switch from "~/components/formElements/Switch/Switch.tsx";
import Headline from "~/components/Headline/Headline.tsx";
import Hint from "~/components/Hint";
import Tooltip from "~/components/Tooltip.tsx";
import type { CreateQuotaConsumerData } from "~/modules/quota/api/quota/quotaTypes.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

// The label data for one assigned order. The parent form resolves it from the
// available-consumers response and passes it in, keyed by order id.
export interface QuotaConsumerOrderInfo {
	orderTitle: string;
	phaseTitle: string;
	projectTitle: string;
	projectId: string;
}

// One field-array entry. React Hook Form adds the `id` key to each entry.
type ConsumerField = CreateQuotaConsumerData & { id: string };

type Props<T extends FieldValues> = {
	control: Control<T>;
	fields: ConsumerField[];
	onAddClick: () => void;
	onRemove: (index: number) => void;
	orderInfoById: Record<string, QuotaConsumerOrderInfo>;
	showEmptyError?: boolean;
	// When set, the section shows this hint in place of the add button.
	addDisabledHint?: string;
	// The consumed budget of each stored consumer, keyed by order id. Only the update form sets it.
	// The section shows the value, and it locks the remove button of an order with consumed budget,
	// because the backend refuses to remove it.
	consumedCentsByOrderId?: Record<string, number>;
	// An error on the whole consumer set. The section shows it above the list.
	budgetErrorMessage?: string;
};

// The assigned-orders section for a quota form. The parent form owns the field
// array and passes `fields`, `onRemove` and `control`. The section renders each
// assigned order with its editable details and a remove control.
const QuotaConsumersFormSection = <T extends FieldValues>({
	control,
	fields,
	onAddClick,
	onRemove,
	orderInfoById,
	showEmptyError,
	addDisabledHint,
	consumedCentsByOrderId,
	budgetErrorMessage,
}: Props<T>) => {
	return (
		<FormSection title="Bestellungen">
			<div className="space-y-4">
				{budgetErrorMessage &&
					<FormInputError>{budgetErrorMessage}</FormInputError>}
				{fields.map((field, index) => {
					const info = orderInfoById[field.orderId];
					const consumedCents = consumedCentsByOrderId?.[field.orderId];
					const isLocked = consumedCents !== undefined && consumedCents > 0;
					const removeButton = <button type="button"
												 className="group flex flex-col items-center justify-center disabled:cursor-not-allowed"
												 disabled={isLocked}
												 onClick={() => onRemove(index)}>
						<TrashIcon className={isLocked
							? "size-5 text-gray-300"
							: "size-5 text-danger-500 group-hover:text-danger-700"} />
					</button>;
					return (
						<div key={field.id}
							 className="rounded-lg border border-gray-300 p-4">
							<div className="flex items-start justify-between gap-x-4">
								<div>
									<Headline type="h5"
											  color="muted">{info?.orderTitle ?? "Unbekannte Bestellung"}</Headline>
									{info &&
										<div className="text-sm text-gray-500">{`${info.projectTitle} · ${info.phaseTitle}`}</div>}
								</div>
								{isLocked ?
									// A disabled button fires no mouse events, so the wrapper receives the hover.
									<Tooltip text="Die Bestellung kann nicht entfernt werden, da bereits Budget verfügt wurde.">
										<span>{removeButton}</span>
									</Tooltip> :
									removeButton}
							</div>
							<div className="mt-3 grid grid-cols-2 gap-x-8">
								<div className="grow">
									<CurrencyInput name={`consumers.${index}.budgetCentsAssigned`}
												   control={control}
												   useControllerError={true}
												   label="Zugewiesenes Budget" />
									{consumedCents !== undefined &&
										<div className="mt-2">
											<CardValue label="Verfügt">
												<span className="text-sm font-medium text-gray-700">{formatCentsToCurrency(consumedCents)}</span>
											</CardValue>
										</div>}
								</div>
								<div className="mt-4 flex h-full grow flex-col justify-start gap-y-1">
									<Switch name={`consumers.${index}.topsUpBudget`}
											control={control}
											label="Erhöht das Budget der Bestellung" />
									<Switch name={`consumers.${index}.timeTrackingEnabled`}
											control={control}
											label="Zeiterfassung aktiv" />
								</div>
							</div>
						</div>
					);
				})}
				{addDisabledHint ?
					<Hint size="sm">
						<p>{addDisabledHint}</p>
					</Hint> :
					<ButtonNewItem size="sm"
								   theme="inline"
								   onClick={onAddClick}
					>{fields.length > 0 && "Weitere "}Bestellung/en auswählen</ButtonNewItem>}
				{showEmptyError &&
					<FormInputError>Einem Abrechnungskontingent muss mindestens eine Bestellung zugewiesen
						werden.</FormInputError>}
			</div>
		</FormSection>
	);
};

export default QuotaConsumersFormSection;
